import React, { useState, useEffect, useRef } from 'react';
import '../styles/Citywise_on_duty_doctors.css';

const getToday = () => {
     const d = new Date();
     return d.toISOString().slice(0, 10);
};

const extractCityFromLocation = (location, cities) => {
     if (!location) return null;
     const found = cities.find(city =>
          location.toLowerCase().includes(city.city.toLowerCase())
     );
     return found ? found.city : null;
};

const formatTimeOnly = (value) => {
     if (!value) return 'N/A';
     const match = String(value).match(/\b(\d{2}:\d{2})(?::\d{2})?\b/);
     if (!match) return 'N/A';
     return new Date(`1970-01-01T${match[1]}:00`).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
     });
};

const getDisplayDoctorName = (name, maxWords = 4) => {
     const fullName = String(name || '').trim();
     if (!fullName) return 'N/A';
     const words = fullName.split(/\s+/);
     if (words.length <= maxWords) return fullName;
     return `${words.slice(0, maxWords).join(' ')}...`;
};

const getShiftTimeRange = (doc) => {
     const inTime = doc?.shift_details?.in_time ? String(doc.shift_details.in_time).slice(0, 5) : 'N/A';
     const outTime = doc?.shift_details?.out_time ? String(doc.shift_details.out_time).slice(0, 5) : 'N/A';
     return `${inTime} - ${outTime}`;
};

const getSufalamStatusClass = (status) => {
     const normalizedStatus = String(status || '').trim().toLowerCase();
     if (normalizedStatus === 'login') return 'status-login';
     if (normalizedStatus === 'logout') return 'status-logout';
     if (normalizedStatus === 'sessiontimeout') return 'status-sessiontimeout';
     return 'status-default';
};

const getNormalizedSufalamHistory = (doc) => {
     if (Array.isArray(doc?.sufalam_history) && doc.sufalam_history.length > 0) {
          return doc.sufalam_history;
     }
     if (Array.isArray(doc?.last_sufalam_record) && doc.last_sufalam_record.length > 0) {
          return doc.last_sufalam_record;
     }
     if (doc?.last_sufalam_record) {
          return [doc.last_sufalam_record];
     }
     return [];
};

const getLatestSufalamRecord = (doc, historyRows) => {
     if (doc?.last_sufalam_latest_record && !Array.isArray(doc.last_sufalam_latest_record)) {
          return doc.last_sufalam_latest_record;
     }
     if (historyRows.length > 0) {
          return historyRows[historyRows.length - 1];
     }
     if (doc?.last_sufalam_record && !Array.isArray(doc.last_sufalam_record)) {
          return doc.last_sufalam_record;
     }
     return null;
};

const isSameSufalamRecord = (a, b) => {
     if (!a || !b) return false;
     if (a.id && b.id) return String(a.id) === String(b.id);
     return (
          String(a.created_date || '') === String(b.created_date || '') &&
          String(a.login_out_status || '') === String(b.login_out_status || '')
     );
};

const getPreviousSufalamHistory = (historyRows, latestRecord) => {
     if (!Array.isArray(historyRows) || historyRows.length === 0) return [];
     if (!latestRecord) return historyRows;

     let skipIndex = -1;
     for (let i = historyRows.length - 1; i >= 0; i -= 1) {
          if (isSameSufalamRecord(historyRows[i], latestRecord)) {
               skipIndex = i;
               break;
          }
     }

     if (skipIndex === -1) return historyRows;
     return historyRows.filter((_, index) => index !== skipIndex);
};

const renderSufalamStatusBadge = (doc) => {
     const historyRows = getNormalizedSufalamHistory(doc);
     const latestRecord = getLatestSufalamRecord(doc, historyRows);

     if (!latestRecord) return null;

     const latestStatusClass = getSufalamStatusClass(latestRecord?.login_out_status);
     const previousHistoryRows = getPreviousSufalamHistory(historyRows, latestRecord);

     return (
          <span className="sufalam-status-wrapper ms-2" tabIndex={0}>
               <span className={`sufalam-status-badge ${latestStatusClass}`}>
                    {latestRecord.login_out_status || 'N/A'} ({formatTimeOnly(latestRecord.created_date)})
               </span>
               <span className="sufalam-history-card">
                    <div className="sufalam-history-title">Previous Events ({previousHistoryRows.length})</div>
                    {previousHistoryRows.length > 0 ? (
                         previousHistoryRows.map((entry, index) => (
                              <div
                                   key={entry?.id || `${entry?.created_date || 'na'}-${entry?.login_out_status || 'na'}-${index}`}
                                   className={`sufalam-history-item ${getSufalamStatusClass(entry?.login_out_status)}`}
                              >
                                   <span>{entry?.login_out_status || 'N/A'}</span>
                                   <span className="sufalam-history-meta">{formatTimeOnly(entry?.created_date)}</span>
                              </div>
                         ))
                    ) : (
                         <div className="sufalam-history-empty">No earlier events</div>
                    )}
               </span>
          </span>
     );
};

const CitywiseOnDutyDoctorsNew = () => {
     const [cities, setCities] = useState([]);
     const [doctors, setDoctors] = useState([]);
     const [loading, setLoading] = useState(true);
     const [isFetching, setIsFetching] = useState(false); // filter changes
     const hasInitialLoad = useRef(false);
     const [selectedDate, setSelectedDate] = useState(getToday());
     const [selectedInTime, setSelectedInTime] = useState('');
     const [selectedOutTime, setSelectedOutTime] = useState('');
     const [dutyStatusFilter, setDutyStatusFilter] = useState('ALL');
     const [searchTerm, setSearchTerm] = useState('');
     const [workTypeFilter, setWorkTypeFilter] = useState('ALL');

     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const token = sessionStorage.getItem('token');

     const getShiftLabel = (doc) => {
          if (doc?.shift_description && String(doc.shift_description).trim()) {
               return String(doc.shift_description).trim();
          }
          if (doc?.shift_details?.shift_name && String(doc.shift_details.shift_name).trim()) {
               return String(doc.shift_details.shift_name).trim();
          }
          const inTime = doc?.shift_details?.in_time ? String(doc.shift_details.in_time).slice(0, 5) : '';
          const outTime = doc?.shift_details?.out_time ? String(doc.shift_details.out_time).slice(0, 5) : '';
          if (inTime || outTime) {
               return `${inTime || 'N/A'} - ${outTime || 'N/A'}`;
          }
          return 'Unknown Shift';
     };

     useEffect(() => {
          const isFirstLoad = !hasInitialLoad.current;
          if (isFirstLoad) setLoading(true);
          else setIsFetching(true);

          const controller = new AbortController();

          fetch(`${BASE_URL}/getCity`, { signal: controller.signal })
               .then((res) => res.json())
               .then((cityRes) => {
                    if (!(cityRes.status && Array.isArray(cityRes.data))) {
                         setCities([]);
                         setDoctors([]);
                         return;
                    }

                    setCities(cityRes.data);

                    const toHHmm = (val) => {
                         if (!val) return '';
                         const m = String(val).match(/^(\d{1,2}):(\d{2})/);
                         if (!m) return '';
                         return `${m[1].padStart(2, '0')}:${m[2]}`;
                    };

                    const inTime = toHHmm(selectedInTime);
                    const outTime = toHHmm(selectedOutTime);

                    const params = new URLSearchParams({ date: selectedDate });
                    if (inTime) params.append('in_time', inTime);
                    if (outTime) params.append('out_time', outTime);

                    return fetch(`${BASE_URL}/doctorCustomSummary?${params.toString()}`, {
                         signal: controller.signal,
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                         },
                    })
                         .then((res) => res.json())
                         .then((docRes) => {
                              if (docRes.status && docRes.data && Array.isArray(docRes.data.details)) {
                                   setDoctors(docRes.data.details);
                              } else {
                                   setDoctors([]);
                              }
                         });
               })
               .catch((err) => {
                    if (err.name !== 'AbortError') {
                         // optional: show toast
                    }
               })
               .finally(() => {
                    if (isFirstLoad) {
                         setLoading(false);
                         hasInitialLoad.current = true;
                    } else {
                         setIsFetching(false);
                    }
               });

          return () => controller.abort();
     }, [BASE_URL, token, selectedDate, selectedInTime, selectedOutTime]);

     const filteredDoctors = doctors.filter(doc => {
          const status = String(doc?.status || '').toLowerCase();
          const isPresent = status === 'present';
          const isAbsent = status === 'absent';
          const hasPunchIn = !!doc?.punch_in;
          const hasPunchOut = !!doc?.punch_out;
          const isSameInOut = hasPunchIn && (!hasPunchOut || doc.punch_in === doc.punch_out);

          if (dutyStatusFilter === 'ACTIVE') {
               // On duty: present + has in-time + not punched out (or same in/out)
               if (!(isPresent && hasPunchIn && isSameInOut)) return false;
          } else if (dutyStatusFilter === 'COMPLETED') {
               // Duty completed: present + valid out-time different from in-time
               if (!(isPresent && hasPunchIn && hasPunchOut && doc.punch_in !== doc.punch_out)) return false;
          } else if (dutyStatusFilter === 'ABSENT') {
               if (!isAbsent) return false;
          }

          // Apply work_type filter
          if (workTypeFilter !== 'ALL') {
               const docWorkType = String(doc?.work_type || '').trim();
               if (docWorkType !== workTypeFilter) return false;
          }

          // Apply search filter across all rendered data
          if (searchTerm.trim()) {
               const search = searchTerm.toLowerCase();
               const docName = String(doc?.employee_name || '').toLowerCase();
               const docLocation = String(doc?.location_name || '').toLowerCase();
               const docCity = String(doc?.city_name || '').toLowerCase();
               const docMobile = String(doc?.mobile || '').toLowerCase();
               const docStatus = String(doc?.status || '').toLowerCase();
               const docPunchIn = String(doc?.punch_in || '').toLowerCase();
               const docPunchOut = String(doc?.punch_out || '').toLowerCase();
               const docShift = getShiftLabel(doc).toLowerCase();

               const matchesSearch = (
                    docName.includes(search) ||
                    docLocation.includes(search) ||
                    docCity.includes(search) ||
                    docMobile.includes(search) ||
                    docStatus.includes(search) ||
                    docPunchIn.includes(search) ||
                    docPunchOut.includes(search) ||
                    docShift.includes(search)
               );

               if (!matchesSearch) return false;
          }

          return true;
     });

     // Group doctors by city (on-duty only)
     const doctorsByCity = {};
     cities.forEach(city => {
          doctorsByCity[city.city] = [];
     });

     filteredDoctors.forEach(doc => {
          let city = doc.city_name;
          if (!city || !city.trim()) {
               city = extractCityFromLocation(doc.location_name, cities);
          }
          if (city && doctorsByCity[city]) {
               doctorsByCity[city].push(doc);
          }
     });

     // Separate Hyderabad from other cities
     const hyderabadCity = cities.find(city => city.city.toLowerCase() === 'hyderabad');
     const otherCities = cities.filter(city => city.city.toLowerCase() !== 'hyderabad');

     if (loading) return <div className="loading">Loading...</div>;



     return (
          <div className="container-fluid py-0">
               {/* <div className='row'>
                    <div className='col-8'>
                         <h5 className='mt-2 mb-2'>Total Doctors on duty: {renderedCount}</h5>
                    </div>
                    <div className='col-4'>
                         <button
                              type="button"
                              className='btn btn-success refresh-btn'
                              onClick={() => window.location.reload()}
                              aria-label="Refresh"
                         >
                              Refresh
                         </button>
                    </div>
               </div> */}
               <div className="row g-2 mb-2 align-items-end">
                    <div className="col-md-1 col-sm-6">
                         <label className="form-label mb-1">Search</label>
                         <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Name, Location, Mobile..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                         />
                    </div>
                    <div className="col-md-1 col-sm-6">
                         <label className="form-label mb-1">Selected Date</label>
                         <input
                              type="date"
                              className="form-control form-control-sm"
                              value={selectedDate}
                              max={getToday()}
                              onChange={(e) => setSelectedDate(e.target.value)}
                         />
                    </div>
                    <div className="col-md-1 col-sm-6">
                         <label className="form-label mb-1">Shift In Time</label>
                         <input
                              type="time"
                              className="form-control form-control-sm"
                              value={selectedInTime}
                              onChange={(e) => setSelectedInTime(e.target.value)}
                         />
                    </div>

                    <div className="col-md-1 col-sm-6">
                         <label className="form-label mb-1">Shift Out Time</label>
                         <input
                              type="time"
                              className="form-control form-control-sm"
                              value={selectedOutTime}
                              onChange={(e) => setSelectedOutTime(e.target.value)}
                         />
                    </div>
                    <div className="col-md-1 col-sm-6">
                         <label className="form-label mb-1">Duty Status</label>
                         <select
                              className="form-select form-select-sm"
                              value={dutyStatusFilter}
                              onChange={(e) => setDutyStatusFilter(e.target.value)}
                         >
                              <option value="ALL">All</option>
                              <option value="ACTIVE">Active / OnDuty</option>
                              <option value="COMPLETED">Present / DutyCompleted</option>
                              <option value="ABSENT">Absent</option>
                         </select>
                    </div>
                    <div className="col-md-1 col-sm-6">
                         <label className="form-label mb-1">Work Type</label>
                         <select
                              className="form-select form-select-sm"
                              value={workTypeFilter}
                              onChange={(e) => setWorkTypeFilter(e.target.value)}
                         >
                              <option value="ALL">All</option>
                              <option value="Regular">Regular</option>
                              <option value="Contract">Contract</option>
                              <option value="Locum">Locum</option>
                         </select>
                    </div>
                    <div className="col-md-1 col-sm-6">
                         <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm w-100"
                              onClick={() => {
                                   setSearchTerm('');
                                   setSelectedDate(getToday());
                                   setSelectedInTime('');
                                   setSelectedOutTime('');
                                   setDutyStatusFilter('ALL');
                                   setWorkTypeFilter('ALL');
                              }}
                         >
                              Reset Filters
                         </button>
                    </div>
               </div>

               {isFetching && <div className="small text-muted">Updating data...</div>}

               <div className="citywise-layout">
                    {/* Hyderabad Section - Full Width */}
                    {hyderabadCity && (
                         <div className="hyderabad-section">
                              <div className="city-brick">
                                   <div className="city-header">{hyderabadCity.city}</div>
                                   {doctorsByCity[hyderabadCity.city] && doctorsByCity[hyderabadCity.city].length > 0 ? (
                                        doctorsByCity[hyderabadCity.city].map((doc, idx) => (
                                             <div
                                                  key={doc.employee_code + idx}
                                                  className="doctor-item"
                                             >
                                                  <span className="fw-semibold doctor-name-hover" tabIndex={0}>
                                                       {getDisplayDoctorName(doc.employee_name)}
                                                       <span className="doctor-hover-card">
                                                            <div><strong>Mobile:</strong> {doc.mobile ? doc.mobile.split(',')[0].trim() : 'N/A'}</div>
                                                            <div><strong>Branch:</strong> {doc.location_name ? doc.location_name : 'N/A'}</div>
                                                            <div><strong>Shift:</strong> {getShiftLabel(doc)}</div>
                                                            <div><strong>Shift Time:</strong> {getShiftTimeRange(doc)}</div>
                                                            <div><strong>Work Type:</strong> {doc.work_type ? doc.work_type : 'N/A'}</div>
                                                       </span>
                                                  </span>
                                                  <span
                                                       className={`ms-2 ${String(doc?.status || '').toLowerCase() === 'absent' ? 'text-danger' : 'text-primary'
                                                            }`}
                                                  >
                                                       {String(doc?.status || '').toLowerCase() === 'absent'
                                                            ? 'Absent'
                                                            : doc.punch_in
                                                                 ? (() => {
                                                                      const match = doc.punch_in.match(/\b(\d{2}:\d{2}(:\d{2})?)\b/);
                                                                      if (match) {
                                                                           const timeStr = match[1].length === 5 ? match[1] + ':00' : match[1];
                                                                           return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString([], {
                                                                                hour: '2-digit',
                                                                                minute: '2-digit',
                                                                                hour12: true
                                                                           });
                                                                      }
                                                                      return 'N/A';
                                                                 })()
                                                                 : 'N/A'}
                                                  </span>
                                                  {doc.punch_out && doc.punch_out !== doc.punch_in && (
                                                       <span className="ms-2 text-primary">
                                                            TO &nbsp;
                                                            {(() => {
                                                                 const match = doc.punch_out.match(/\b(\d{2}:\d{2}(:\d{2})?)\b/);
                                                                 if (match) {
                                                                      const timeStr = match[1].length === 5 ? match[1] + ':00' : match[1];
                                                                      return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString([], {
                                                                           hour: '2-digit',
                                                                           minute: '2-digit',
                                                                           hour12: true
                                                                      });
                                                                 }
                                                                 return 'N/A';
                                                            })()}
                                                       </span>
                                                  )}
                                                  {renderSufalamStatusBadge(doc)}
                                             </div>
                                        ))
                                   ) : (
                                        <div className="text-muted text-center" style={{ fontSize: '0.9em' }}>
                                             No on-duty doctors
                                        </div>
                                   )}
                              </div>
                         </div>
                    )}

                    {/* Other Cities - Two Column Masonry */}
                    <div className="citywise-masonry">
                         {otherCities.map(city => (
                              <div key={city.city} className="city-brick">
                                   <div className="city-header">{city.city}</div>
                                   {doctorsByCity[city.city] && doctorsByCity[city.city].length > 0 ? (
                                        doctorsByCity[city.city].map((doc, idx) => (
                                             <div
                                                  key={doc.employee_code + idx}
                                                  className="doctor-item"

                                             >
                                                  <span className="fw-semibold doctor-name-hover" tabIndex={0}>
                                                       {getDisplayDoctorName(doc.employee_name)}
                                                       <span className="doctor-hover-card">
                                                            <div><strong>Mobile:</strong> {doc.mobile ? doc.mobile.split(',')[0].trim() : 'N/A'}</div>
                                                            <div><strong>Branch:</strong> {doc.location_name ? doc.location_name : 'N/A'}</div>
                                                            <div><strong>Shift:</strong> {getShiftLabel(doc)}</div>
                                                            <div><strong>Shift Time:</strong> {getShiftTimeRange(doc)}</div>
                                                            <div><strong>Work Type:</strong> {doc.work_type ? doc.work_type : 'N/A'}</div>
                                                       </span>
                                                  </span>
                                                  <span
                                                       className={`ms-2 ${String(doc?.status || '').toLowerCase() === 'absent' ? 'text-danger' : 'text-primary'
                                                            }`}
                                                  >
                                                       {String(doc?.status || '').toLowerCase() === 'absent'
                                                            ? 'Absent'
                                                            : doc.punch_in
                                                                 ? (() => {
                                                                      const match = doc.punch_in.match(/\b(\d{2}:\d{2}(:\d{2})?)\b/);
                                                                      if (match) {
                                                                           const timeStr = match[1].length === 5 ? match[1] + ':00' : match[1];
                                                                           return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString([], {
                                                                                hour: '2-digit',
                                                                                minute: '2-digit',
                                                                                hour12: true
                                                                           });
                                                                      }
                                                                      return 'N/A';
                                                                 })()
                                                                 : 'N/A'}
                                                  </span>
                                                  {doc.punch_out && doc.punch_out !== doc.punch_in && (
                                                       <span className="ms-2 text-primary">
                                                            TO &nbsp;
                                                            {(() => {
                                                                 const match = doc.punch_out.match(/\b(\d{2}:\d{2}(:\d{2})?)\b/);
                                                                 if (match) {
                                                                      const timeStr = match[1].length === 5 ? match[1] + ':00' : match[1];
                                                                      return new Date(`1970-01-01T${timeStr}`).toLocaleTimeString([], {
                                                                           hour: '2-digit',
                                                                           minute: '2-digit',
                                                                           hour12: true
                                                                      });
                                                                 }
                                                                 return 'N/A';
                                                            })()}
                                                       </span>
                                                  )}
                                                  {renderSufalamStatusBadge(doc)}
                                             </div>
                                        ))
                                   ) : (
                                        <div className="text-muted text-center" style={{ fontSize: '0.9em' }}>
                                             No on-duty doctors
                                        </div>
                                   )}
                              </div>
                         ))}
                    </div>
               </div>
          </div >
     );
};

export default CitywiseOnDutyDoctorsNew;